import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Spinner } from '@/components/ui/Spinner';
import { useSettings, useUpdateSettings, type Settings } from './useSettings';

const STRATEGIES = ['round_robin', 'by_area', 'by_campaign', 'by_team', 'by_performance'] as const;

/** Terminal stages carry no forecast probability — see validate_settings(). */
const FORECAST_STAGES = [
  'new',
  'contacted',
  'qualified',
  'interested',
  'follow_up',
  'meeting',
  'site_visit',
  'offer',
  'negotiation',
  'reservation',
  'contract',
] as const;

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <fieldset className="rounded-xl border border-slate-200 bg-white p-4">
      <legend className="px-2 text-sm font-semibold text-slate-800">{title}</legend>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{children}</div>
    </fieldset>
  );
}

export default function SettingsPage() {
  const { t } = useTranslation();
  const { data, isPending, isError } = useSettings();
  const update = useUpdateSettings();

  const [form, setForm] = useState<Settings | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  if (isPending || !form) return <Spinner />;
  if (isError) return <Alert tone="error">{t('common.error')}</Alert>;

  const num = (key: keyof Settings) => (value: string) =>
    setForm((f) => (f ? { ...f, [key]: Number(value) } : f));

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setFailure(null);
    setSaved(false);

    try {
      // Send the whole form; admin_update_settings() merges it over the stored row and
      // the settings_validate trigger has the final say on whether it is coherent.
      const { id: _id, ...patch } = form;
      await update.mutateAsync(patch);
      setSaved(true);
    } catch (cause) {
      setFailure(cause instanceof Error ? cause.message : t('common.error'));
    }
  };

  return (
    <form onSubmit={(e) => void onSubmit(e)} className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-bold text-slate-900">{t('nav.settings')}</h1>
        <Button type="submit" loading={update.isPending}>
          {t('common.save')}
        </Button>
      </div>

      <p className="text-sm text-slate-500">{t('admin.settings.intro')}</p>

      {failure ? <Alert tone="error">{failure}</Alert> : null}
      {saved ? <Alert tone="success">{t('admin.settings.saved')}</Alert> : null}

      <Section title={t('admin.settings.slaSection')}>
        <Input
          label={t('admin.settings.slaMinutes')}
          type="number"
          min={1}
          max={1440}
          value={form.sla_first_contact_minutes}
          onChange={(e) => num('sla_first_contact_minutes')(e.target.value)}
        />
        <Input
          label={t('admin.settings.escalation')}
          hint={t('admin.settings.escalationHint')}
          value={form.sla_escalation_minutes.join(', ')}
          onChange={(e) =>
            setForm((f) =>
              f
                ? {
                    ...f,
                    sla_escalation_minutes: e.target.value
                      .split(/[،,]/)
                      .map((v) => Number(v.trim()))
                      .filter((v) => Number.isFinite(v) && v > 0),
                  }
                : f,
            )
          }
        />
        <Select
          label={t('admin.settings.assignment')}
          value={form.assignment_strategy}
          onChange={(e) => setForm((f) => (f ? { ...f, assignment_strategy: e.target.value } : f))}
          options={STRATEGIES.map((s) => ({
            value: s,
            label: t(`admin.settings.strategies.${s}` as 'admin.settings.strategies.round_robin'),
          }))}
        />
      </Section>

      <Section title={t('admin.settings.healthSection')}>
        <Input
          label={t('admin.settings.atRisk')}
          type="number"
          min={1}
          value={form.health_at_risk_days}
          onChange={(e) => num('health_at_risk_days')(e.target.value)}
        />
        <Input
          label={t('admin.settings.dormant')}
          type="number"
          min={1}
          value={form.health_dormant_days}
          onChange={(e) => num('health_dormant_days')(e.target.value)}
        />
        <Input
          label={t('admin.settings.dead')}
          type="number"
          min={1}
          value={form.health_dead_days}
          onChange={(e) => num('health_dead_days')(e.target.value)}
        />
      </Section>

      <Section title={t('admin.settings.leakageSection')}>
        <Input
          label={t('admin.settings.uncontactedHours')}
          type="number"
          min={1}
          value={form.leakage_uncontacted_hours}
          onChange={(e) => num('leakage_uncontacted_hours')(e.target.value)}
        />
        <Input
          label={t('admin.settings.stalledDays')}
          type="number"
          min={1}
          value={form.leakage_stalled_days}
          onChange={(e) => num('leakage_stalled_days')(e.target.value)}
        />
      </Section>

      <Section title={t('admin.settings.commissionSection')}>
        <Input
          label={t('admin.settings.companyShare')}
          type="number"
          step="0.01"
          value={form.commission_company_percent}
          onChange={(e) =>
            setForm((f) => (f ? { ...f, commission_company_percent: e.target.value } : f))
          }
        />
        <Input
          label={t('admin.settings.agentShare')}
          type="number"
          step="0.01"
          value={form.commission_agent_percent}
          onChange={(e) =>
            setForm((f) => (f ? { ...f, commission_agent_percent: e.target.value } : f))
          }
        />
        <Input
          label={t('admin.settings.leaderShare')}
          type="number"
          step="0.01"
          hint={t('admin.settings.sharesHint')}
          value={form.commission_team_leader_percent}
          onChange={(e) =>
            setForm((f) => (f ? { ...f, commission_team_leader_percent: e.target.value } : f))
          }
        />
      </Section>

      <Section title={t('admin.settings.retentionSection')}>
        <Input
          label={t('admin.settings.retentionAudit')}
          type="number"
          min={1}
          hint={t('admin.settings.retentionHint')}
          value={form.retention_audit_months}
          onChange={(e) => num('retention_audit_months')(e.target.value)}
        />
        <Input
          label={t('admin.settings.retentionActivity')}
          type="number"
          min={1}
          value={form.retention_activity_months}
          onChange={(e) => num('retention_activity_months')(e.target.value)}
        />
        <Input
          label={t('admin.settings.retentionNotifications')}
          type="number"
          min={7}
          value={form.retention_notification_days}
          onChange={(e) => num('retention_notification_days')(e.target.value)}
        />
      </Section>

      <Section title={t('admin.settings.forecastSection')}>
        {FORECAST_STAGES.map((stage) => (
          <Input
            key={stage}
            label={t(`stages.${stage}` as 'stages.new')}
            type="number"
            min={0}
            max={100}
            step="1"
            value={Math.round((form.stage_close_probability[stage] ?? 0) * 100)}
            onChange={(e) =>
              setForm((f) =>
                f
                  ? {
                      ...f,
                      stage_close_probability: {
                        ...f.stage_close_probability,
                        [stage]: Number(e.target.value) / 100,
                      },
                    }
                  : f,
              )
            }
          />
        ))}
      </Section>
    </form>
  );
}
